import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ITimezone } from "react-timezone-select";
import { productQueries, Product } from "@/api/product";
import { CreateJob } from "@/api/scheduledJobs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import YouTubeInput from "@/components/YouTubeInput";
import { TimeAndTzPicker } from "@/components/TimeAndTzPicker";
import { TweetSelector } from "./TweetSelector";

/** Return a random minute between 1..9 */
function getRandomMinute() {
  return Math.floor(Math.random() * 9) + 1;
}

/**
 * A sub-component for displaying one product card in a "catalog."
 */
function ProductCard({
  product,
  onSelect,
  selected,
}: {
  product: Product;
  onSelect: (p: Product) => void;
  selected: boolean;
}) {
  return (
    <div
      className={`
        border border-border rounded-lg p-3 cursor-pointer
        bg-[color:rgba(255,255,255,0.05)]
        hover:shadow-md transition-shadow overflow-hidden
        ${selected ? "border-primary" : ""}
      `}
      onClick={() => onSelect(product)}
    >
      <div className="font-semibold">{product.name}</div>
      <div className="text-xs text-muted-foreground mt-1">
        {product.description}
      </div>
      <div className="mt-2">
        <Badge variant="outline">Memetica Credits: {product.credit_cost}</Badge>
      </div>
    </div>
  );
}

interface AddJobDialogProps {
  onClose: () => void;
  onSave: (job: CreateJob) => Promise<void>;
  selectedPersonaId: string;
  initialProduct?: string; // Optional prop to pre-select a product
  initialTweetId?: string; // Optional prop to pre-select a tweet
  showProductSelector?: boolean;
}

/**
 * The complete AddJobDialog, with:
 *  - product "catalog" to select which product_sku
 *  - dynamic metadata fields (topic, YouTube, etc.)
 *  - zero-padded minutes
 *  - a TimezoneSelect dropdown from `react-timezone-select`
 *  - integrated YouTubeInput for video validation/preview
 */
export function AddJobDialog({
  onClose,
  onSave,
  selectedPersonaId,
  initialProduct,
  initialTweetId,
  showProductSelector = true,
}: AddJobDialogProps) {
  const { toast } = useToast();

  // 1) We fetch all products
  const {
    data: load_products = [],
    isLoading: isLoadingProducts,
    error: productsError,
  } = useQuery(productQueries.products());

  // 2) Filter the products to these SKUs
  const agent_led_tools = [
    "twitter-playground-create-news-post",
    "twitter-playground-create-worldview-post",
    "twitter-posting-create-image-post",
    "twitter-posting-create-multimodal-post",
    "twitter-posting-create-news-post",
    "twitter-posting-create-worldview-post",
    "twitter-threads-custom-topic",
    "twitter-threads-youtube-threader",
    "twitter-engagement-engage-with-kols",
    "twitter-posting-post-existing-tweet",
    "instagram-posting-post-existing-content",
  ];
  const products = useMemo(() => {
    // Add our custom product if it doesn't exist in the API response
    const existingProducts = load_products.filter((p) =>
      agent_led_tools.includes(p.product_sku),
    );

    // Check if our new product is already in the list
    const hasPostExistingTweet = existingProducts.some(
      (p) => p.product_sku === "twitter-posting-post-existing-tweet",
    );

    const hasPostExistingContentToInstagram = existingProducts.some(
      (p) => p.product_sku === "instagram-posting-post-existing-content",
    );

    // If not, add it manually
    // TODO this shouldnt be needed, we should refactor this component to make it simpler
    if (!hasPostExistingTweet) {
      // @ts-expect-error missing property
      existingProducts.push({
        product_sku: "twitter-posting-post-existing-tweet",
        name: "Post Existing Tweet",
        description:
          "Schedule an existing tweet to be posted at a specific time",
        credit_cost: 10,
      });
    }

    // TODO this shouldnt be needed, we should refactor this component to make it simpler
    if (!hasPostExistingContentToInstagram) {
      // @ts-expect-error missing property
      existingProducts.push({
        product_sku: "instagram-posting-post-existing-content",
        name: "Post Existing Content",
        description:
          "Schedule an existing Instagram post to be posted at a specific time",
        credit_cost: 10,
      });
    }

    return existingProducts;
  }, [load_products]);

  // Find initial product if specified
  const foundInitialProduct = useMemo(() => {
    if (!initialProduct) return null;
    return products.find((p) => p.product_sku === initialProduct) || null;
  }, [products, initialProduct]);

  // Local states
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(
    foundInitialProduct,
  );

  // Oneoff vs. recurring
  const [recurringOrOneOff, setRecurringOrOneOff] = useState<
    "oneoff" | "recurring"
  >("oneoff");
  const [recurringFrequency, setRecurringFrequency] = useState<
    "" | "daily" | "weekly"
  >("");

  // Time fields
  const [hour, setHour] = useState<number>(12);
  const [minute, setMinute] = useState<number>(getRandomMinute());

  // For "twitter-engagement-engage-with-kols"
  const [kolsCount, setKolsCount] = useState<number>(1);

  // For topic-based products
  const [topic, setTopic] = useState<string>("");

  const [tone, setTone] = useState<string>("");

  // For youtube-based product
  const [youtubeUrl, setYoutubeUrl] = useState<string>("");

  // For tweet-selection product
  const [selectedTweetId, setSelectedTweetId] = useState<string | null>(
    initialTweetId || null,
  );

  // Manage saving spinner
  const [isSaving, setIsSaving] = useState(false);

  // Use local or object from react-timezone-select
  const localTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const [timeZone, setTimeZone] = useState<ITimezone | string>(localTz);

  // If the chosen product allows recurring
  const productAllowsRecurring = useMemo(() => {
    if (!selectedProduct) return false;
    const sku = selectedProduct.product_sku;
    if (
      sku === "twitter-threads-custom-topic" ||
      sku === "twitter-threads-youtube-threader" ||
      sku === "twitter-posting-post-existing-tweet" ||
      sku === "instagram-posting-post-existing-content"
    ) {
      return false;
    }
    return true;
  }, [selectedProduct]);

  const productNeedsKolsSlider = useMemo(() => {
    return (
      selectedProduct?.product_sku === "twitter-engagement-engage-with-kols"
    );
  }, [selectedProduct]);

  const productTopicIsRequired = useMemo(() => {
    return selectedProduct?.product_sku === "twitter-threads-custom-topic";
  }, [selectedProduct]);

  const productTopicIsOptional = useMemo(() => {
    if (!selectedProduct) return false;
    const optionalSkus = [
      "twitter-playground-create-news-post",
      "twitter-playground-create-worldview-post",
      "twitter-posting-create-news-post",
      "twitter-posting-create-worldview-post",
      "twitter-posting-create-image-post",
    ];
    return optionalSkus.includes(selectedProduct.product_sku);
  }, [selectedProduct]);

  const productToneIsOptional = useMemo(() => {
    if (!selectedProduct) return false;
    const optionalSkus = [
      "twitter-playground-create-news-post",
      "twitter-playground-create-worldview-post",
      "twitter-posting-create-news-post",
      "twitter-posting-create-worldview-post",
      "twitter-engagement-engage-with-kols",
    ];
    return optionalSkus.includes(selectedProduct.product_sku);
  }, [selectedProduct]);

  const productNeedsYoutubeUrl = useMemo(() => {
    return selectedProduct?.product_sku === "twitter-threads-youtube-threader";
  }, [selectedProduct]);

  // Add this new check for tweet selection
  const productNeedsTweetSelection = useMemo(() => {
    return (
      selectedProduct?.product_sku === "twitter-posting-post-existing-tweet" ||
      selectedProduct?.product_sku === "instagram-posting-post-existing-content"
    );
  }, [selectedProduct]);

  /**
   * Final create
   */
  const handleCreateJob = async () => {
    try {
      if (!selectedProduct) {
        toast({ description: "Please select a product." });
        return;
      }

      if (recurringOrOneOff === "recurring" && !recurringFrequency) {
        toast({ description: "Please select daily or weekly for recurring." });
        return;
      }

      if (!productAllowsRecurring) {
        setRecurringOrOneOff("oneoff");
      }

      if (productTopicIsRequired && !topic) {
        toast({ description: "Topic is required for this product." });
        return;
      }

      if (productNeedsYoutubeUrl && !youtubeUrl) {
        toast({ description: "YouTube URL is required for this product." });
        return;
      }

      // Add validation for tweet selection
      if (productNeedsTweetSelection && !selectedTweetId) {
        toast({ description: "Please select a tweet to post." });
        return;
      }

      const hh = hour.toString().padStart(2, "0");
      const mm = minute.toString().padStart(2, "0");

      let finalTz = "UTC";
      if (typeof timeZone === "string") {
        finalTz = timeZone;
      } else if (timeZone && typeof timeZone.value === "string") {
        finalTz = timeZone.value;
      }

      const newJob: CreateJob = {
        agent_id: selectedPersonaId,
        product_sku: selectedProduct.product_sku,
        job_name: selectedProduct.name,
        recurring_or_one_off: recurringOrOneOff,
        recurring_frequency:
          recurringOrOneOff === "recurring" ? recurringFrequency : "",
        generated_by: "human",
        metadata: {},
        status: "pending",
        timezone: finalTz,
        execution_time_in_tz: `${hh}:${mm}`,
      };

      if (productNeedsKolsSlider) {
        newJob.metadata.kols_count = kolsCount;
      }
      if (productNeedsYoutubeUrl) {
        newJob.metadata.youtube_url = youtubeUrl;
      }
      if (productTopicIsRequired || productTopicIsOptional) {
        newJob.metadata.topic = topic;
      }
      if (productToneIsOptional) {
        newJob.metadata.tone = tone;
      }
      // Add selected tweet ID to metadata
      if (productNeedsTweetSelection) {
        newJob.metadata.tweet_id = selectedTweetId;
      }

      setIsSaving(true);
      await onSave(newJob);
      onClose();
    } catch (err) {
      console.error("Error saving job:", err);
      toast({ description: "Failed to create job." });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[80vw] grid-rows-[auto_1fr_auto]">
        <DialogHeader className="shrink-0">
          <DialogTitle>Create New Job</DialogTitle>
          <DialogDescription>
            Configure your scheduled job below. You can pick a product, set a
            time, and choose your timezone from the dropdown.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto">
          {isLoadingProducts && (
            <div className="flex justify-center items-center min-h-[200px]">
              Loading products...
            </div>
          )}

          {productsError && (
            <div className="text-red-500">
              Error loading products: {String(productsError)}
            </div>
          )}

          {!isLoadingProducts && !productsError && (
            <div className="space-y-6">
              {showProductSelector && (
                <>
                  {/* 1) Product Catalog */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                    {products.map((prod) => (
                      <ProductCard
                        key={prod.product_sku}
                        product={prod}
                        onSelect={setSelectedProduct}
                        selected={
                          selectedProduct?.product_sku === prod.product_sku
                        }
                      />
                    ))}
                  </div>
                </>
              )}

              {selectedProduct && (
                <Card className="p-4 space-y-4 bg-[color:rgba(255,255,255,0.05)]">
                  <div className="text-md font-semibold">
                    Configure {selectedProduct.name}
                  </div>
                  <div className="text-sm text-muted-foreground mb-2">
                    {selectedProduct.description}
                  </div>

                  {productAllowsRecurring ? (
                    <div className="flex flex-wrap items-center gap-4">
                      <Label>Schedule Type:</Label>
                      <Button
                        variant={
                          recurringOrOneOff === "oneoff" ? "default" : "outline"
                        }
                        onClick={() => {
                          setRecurringOrOneOff("oneoff");
                          setRecurringFrequency("");
                        }}
                      >
                        One-off
                      </Button>
                      <Button
                        variant={
                          recurringOrOneOff === "recurring"
                            ? "default"
                            : "outline"
                        }
                        onClick={() => setRecurringOrOneOff("recurring")}
                      >
                        Recurring
                      </Button>
                      {recurringOrOneOff === "recurring" && (
                        <div className="flex items-center gap-2">
                          <Label>Every:</Label>
                          <Button
                            variant={
                              recurringFrequency === "daily"
                                ? "default"
                                : "outline"
                            }
                            onClick={() => setRecurringFrequency("daily")}
                          >
                            Daily
                          </Button>
                          <Button
                            variant={
                              recurringFrequency === "weekly"
                                ? "default"
                                : "outline"
                            }
                            onClick={() => setRecurringFrequency("weekly")}
                          >
                            Weekly
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <Label>Schedule:</Label>
                      <p className="text-sm text-muted-foreground">
                        This product is one-off only.
                      </p>
                    </div>
                  )}

                  {/* Time + Timezone */}
                  <TimeAndTzPicker
                    hour={hour}
                    setHour={setHour}
                    minute={minute}
                    setMinute={setMinute}
                    timeZone={timeZone}
                    setTimeZone={setTimeZone}
                  />

                  {/* Extra fields */}
                  {productNeedsKolsSlider && (
                    <div>
                      <Label>KOLs to engage: {kolsCount}</Label>
                      <Slider
                        min={1}
                        max={5}
                        step={1}
                        value={[kolsCount]}
                        onValueChange={([val]) => setKolsCount(val)}
                        className="max-w-sm mt-2"
                      />
                    </div>
                  )}

                  {productNeedsYoutubeUrl && (
                    <div>
                      <Label>YouTube URL (Required)</Label>
                      <YouTubeInput
                        value={youtubeUrl}
                        onChange={(val) => setYoutubeUrl(val)}
                        isLoading={isSaving}
                      />
                    </div>
                  )}

                  {(productTopicIsRequired || productTopicIsOptional) && (
                    <div>
                      <Label>
                        Topic{productTopicIsRequired ? " (Required)" : ""}
                      </Label>
                      <Textarea
                        className="mt-1"
                        placeholder={
                          productTopicIsRequired
                            ? "Enter topic for custom threads..."
                            : "Enter topic or leave empty if none"
                        }
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                      />
                    </div>
                  )}

                  {productToneIsOptional && (
                    <div>
                      <Label>
                        Tone{productToneIsOptional ? " (Optional)" : ""}
                      </Label>
                      <Textarea
                        className="mt-1"
                        placeholder={"Enter tone of message..."}
                        value={tone}
                        onChange={(e) => setTone(e.target.value)}
                      />
                    </div>
                  )}

                  {/* Tweet Selection UI */}
                  {productNeedsTweetSelection && (
                    <div>
                      <Label>Select Tweet to Post (Required)</Label>
                      <TweetSelector
                        personaId={selectedPersonaId}
                        onSelectTweet={setSelectedTweetId}
                        selectedTweetId={selectedTweetId}
                        highlightTweetId={initialTweetId}
                      />
                    </div>
                  )}
                </Card>
              )}
            </div>
          )}
        </div>

        {!isLoadingProducts && !productsError && (
          <DialogFooter className="gap-y-2 shrink-0">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              disabled={!selectedProduct || isSaving}
              onClick={handleCreateJob}
            >
              {isSaving ? "Creating..." : "Create Job"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
